import { getPageShareMirror } from '../store/pageShareStateStore';
import { persistantStore } from '../store/persistantStore';

export { DASHBOARD_SHARE_VERSION } from './shareApplyUtils';

export interface ShareContext {
  selectedMenu: string;
  selectedDatasetId: number;
  selectedSourceId: number;
  selectedDate: string;
}

export interface SharePageDefinition {
  pageKey: string;
  resolveComponentPath: (ctx: ShareContext) => string;
  menuMatch: (normalizedMenu: string, ctx: ShareContext) => boolean;
}

export function normalizeMenuKey(menu: string): string {
  return (menu || '').replace(/\s+/g, '').replace(/_/g, '').toUpperCase();
}

export function pageKeyFromComponentPath(componentPath: string): string | null {
  const path = componentPath.replace(/\.(tsx|ts|jsx|js)$/i, '').toLowerCase();
  const entry = SHARE_PAGE_REGISTRY.find((def) => def.resolveComponentPath(buildShareContext()).toLowerCase() === path);
  if (entry) {
    return entry.pageKey;
  }
  const tail = path.split('/').pop() ?? '';
  const byTail: Record<string, string> = {
    fleetperformance: 'FleetPerformance',
    performance: 'Performance',
    fleetmaneuvers: 'FleetManeuvers',
    maneuvers: 'Maneuvers',
    racesummary: 'RaceSummary',
    trainingsummary: 'TrainingSummary',
    prestarts: 'DatasetPrestarts',
    prestart: 'Prestart',
    fleetcalibration: 'FleetCalibration',
    fleetcheatsheet: 'FleetCheatSheet',
    cheatsheet: 'CheatSheet',
    crewhours: 'CrewHours',
    performancedrivers: 'PerformanceDrivers',
    eventracesummary: 'EventRaceSummary',
    fleetperformancehistory: 'FleetPerformanceHistory',
    performancehistory: 'PerformanceHistory',
    fleetmaneuvershistory: 'FleetManeuversHistory',
    maneuvershistory: 'ManeuversHistory',
    fleetmap: 'FleetMap',
  };
  return byTail[tail.replace(/\s/g, '')] ?? null;
}

function buildShareContext(): ShareContext {
  return {
    selectedMenu: persistantStore.selectedMenu(),
    selectedDatasetId: persistantStore.selectedDatasetId(),
    selectedSourceId: persistantStore.selectedSourceId(),
    selectedDate: persistantStore.selectedDate() || '',
  };
}

/** Most specific matchers first. */
export const SHARE_PAGE_REGISTRY: SharePageDefinition[] = [
  {
    pageKey: 'ExploreMap',
    resolveComponentPath: () => 'gp50/dataset/explore/Map',
    menuMatch: (m, ctx) => (m === 'MAP' || m === 'OVERLAY') && ctx.selectedDatasetId > 0,
  },
  {
    pageKey: 'FleetMap',
    resolveComponentPath: () => 'gp50/day/explore/FleetMap',
    menuMatch: (m, ctx) =>
      (m === 'MAP' || m === 'OVERLAY') &&
      ctx.selectedDatasetId === 0 &&
      ctx.selectedDate.trim() !== '',
  },
  {
    pageKey: 'PerformanceDrivers',
    resolveComponentPath: () => 'gp50/project/all/reports/PerformanceDrivers',
    menuMatch: (m) => m.includes('PERFORMANCE') && m.includes('DRIVER'),
  },
  {
    pageKey: 'EventRaceSummary',
    resolveComponentPath: () => 'gp50/project/all/reports/EventRaceSummary',
    menuMatch: (m) => m.includes('EVENT') && m.includes('SUMMARY'),
  },
  {
    pageKey: 'FleetPerformanceHistory',
    resolveComponentPath: () => 'gp50/project/all/reports/FleetPerformanceHistory',
    menuMatch: (m, ctx) => m.includes('PERFORMANCE') && m.includes('HISTORY') && ctx.selectedSourceId === 0,
  },
  {
    pageKey: 'PerformanceHistory',
    resolveComponentPath: () => 'gp50/project/source/reports/PerformanceHistory',
    menuMatch: (m, ctx) => m.includes('PERFORMANCE') && m.includes('HISTORY') && ctx.selectedSourceId > 0,
  },
  {
    pageKey: 'FleetManeuversHistory',
    resolveComponentPath: () => 'gp50/project/all/reports/FleetManeuversHistory',
    menuMatch: (m, ctx) => m.includes('MANEUVER') && m.includes('HISTORY') && ctx.selectedSourceId === 0,
  },
  {
    pageKey: 'ManeuversHistory',
    resolveComponentPath: () => 'gp50/project/source/reports/ManeuversHistory',
    menuMatch: (m, ctx) => m.includes('MANEUVER') && m.includes('HISTORY') && ctx.selectedSourceId > 0,
  },
  {
    pageKey: 'FleetCheatSheet',
    resolveComponentPath: () => 'gp50/project/all/reports/FleetCheatSheet',
    menuMatch: (m, ctx) => m.includes('CHEAT') && ctx.selectedSourceId === 0,
  },
  {
    pageKey: 'CheatSheet',
    resolveComponentPath: () => 'gp50/project/source/reports/CheatSheet',
    menuMatch: (m, ctx) => m.includes('CHEAT') && ctx.selectedSourceId > 0,
  },
  {
    pageKey: 'CrewHours',
    resolveComponentPath: () => 'gp50/project/source/reports/CrewHours',
    menuMatch: (m, ctx) => m.includes('CREW') && m.includes('HOUR') && ctx.selectedSourceId > 0,
  },
  {
    pageKey: 'TrainingSummary',
    resolveComponentPath: () => 'gp50/day/reports/TrainingSummary',
    menuMatch: (m) => m.includes('TRAINING') && m.includes('SUMMARY'),
  },
  {
    pageKey: 'RaceSummary',
    resolveComponentPath: () => 'gp50/day/reports/RaceSummary',
    menuMatch: (m) => m.includes('RACE') && m.includes('SUMMARY'),
  },
  {
    pageKey: 'DatasetPrestarts',
    resolveComponentPath: () => 'gp50/dataset/reports/Prestarts',
    menuMatch: (m, ctx) => m === 'PRESTARTS' && ctx.selectedDatasetId > 0,
  },
  {
    pageKey: 'Prestart',
    resolveComponentPath: () => 'gp50/day/reports/Prestart',
    menuMatch: (m) => m === 'PRESTART' || m.includes('STARTSUMMARY') || (m.includes('START') && m.includes('SUMMARY')),
  },
  {
    pageKey: 'FleetCalibration',
    resolveComponentPath: () => 'gp50/day/reports/FleetCalibration',
    menuMatch: (m) => m === 'CALIBRATION',
  },
  {
    pageKey: 'Performance',
    resolveComponentPath: () => 'gp50/dataset/reports/Performance',
    menuMatch: (m, ctx) => m === 'PERFORMANCE' && ctx.selectedDatasetId > 0,
  },
  {
    pageKey: 'FleetPerformance',
    resolveComponentPath: () => 'gp50/day/reports/FleetPerformance',
    menuMatch: (m, ctx) => m === 'PERFORMANCE' && ctx.selectedDatasetId === 0 && ctx.selectedDate.trim() !== '',
  },
  {
    pageKey: 'Maneuvers',
    resolveComponentPath: () => 'gp50/dataset/reports/Maneuvers',
    menuMatch: (m, ctx) => m === 'MANEUVERS' && ctx.selectedDatasetId > 0,
  },
  {
    pageKey: 'FleetManeuvers',
    resolveComponentPath: () => 'gp50/day/reports/FleetManeuvers',
    menuMatch: (m, ctx) =>
      m === 'MANEUVERS' &&
      ctx.selectedDatasetId === 0 &&
      (ctx.selectedDate.trim() !== '' || ctx.selectedSourceId === 0),
  },
];

export function getSharePageDefinition(pageKey: string): SharePageDefinition | undefined {
  return SHARE_PAGE_REGISTRY.find((def) => def.pageKey === pageKey);
}

export function getShareContext(): ShareContext {
  return buildShareContext();
}

export function resolveShareComponentPath(pageKey: string, ctx: ShareContext = getShareContext()): string | null {
  const def = getSharePageDefinition(pageKey);
  return def ? def.resolveComponentPath(ctx) : null;
}

export function inferSharePageKey(ctx: ShareContext = getShareContext()): string | null {
  const normalizedMenu = normalizeMenuKey(ctx.selectedMenu);

  for (const def of SHARE_PAGE_REGISTRY) {
    const mirror = getPageShareMirror(def.pageKey);
    if (mirror && Object.keys(mirror).length > 0 && def.menuMatch(normalizedMenu, ctx)) {
      return def.pageKey;
    }
  }

  for (const def of SHARE_PAGE_REGISTRY) {
    const mirror = getPageShareMirror(def.pageKey);
    if (mirror && Object.keys(mirror).length > 0) {
      return def.pageKey;
    }
  }

  if (!normalizedMenu) {
    return null;
  }

  for (const def of SHARE_PAGE_REGISTRY) {
    if (def.menuMatch(normalizedMenu, ctx)) {
      return def.pageKey;
    }
  }

  return null;
}
